/**
 * Mall Directory — "Das digitale Einkaufszentrum".
 *
 * Allyver wird als EIN Einkaufszentrum inszeniert: ein leuchtendes
 * Dach-/Eingangsschild oben, darunter eine Fassade aus Schaufenster-Kacheln
 * (eine pro Kategorie). Jede Kachel hat eine Markise (Awning), ein
 * beleuchtetes Schaufenster (Glow), Icon, Kategorie-Name und Caption.
 *
 * Motion:
 *   - Das Dach-Schild „zieht" aus der Mitte auf (scaleX) und glüht an.
 *   - Headline steigt darunter ein.
 *   - Kachel für Kachel öffnet das Center: Kachel poppt hoch, die Markise
 *     rollt von oben aus (scaleY), dann „geht das Licht an" (Schaufenster-
 *     Glow blendet ein), Icon snappt rein, Label + Caption steigen ein.
 *   - Zum Schluss wandert ein weicher Lichtstreifen über die Fassade.
 *
 * Items-Format (wie categoryGrid):  "Titel | Caption | iconKey"
 *   z. B. "Restaurants | Tisch buchen | shop"
 *
 * Extras:
 *   - extras.brand  → Schild-Text (Default: Markenname)
 *   - extras.tagline → kleine Zeile unter dem Schild-Text
 */
import type { SceneBuilder } from "../../reel/scenes.js";
import { brandBackdrop } from "../../primitives/brandBackdrop.js";
import {
  accentFor, brandName, clipOutroFade, combineSceneFragments, escapeHtml,
  gsapSetLine, makeTl, typeSize, weights, withAlpha, colors as themeColors,
} from "../../reel/sceneShared.js";
import { lucideIcon, splitItem } from "./_mockHelpers.js";

const ICON_MAP: Record<string, string> = {
  restaurants: "shop", restaurant: "shop", food: "shop", essen: "shop",
  shop: "store", shops: "store", produkte: "store", shopping: "store", händler: "store", haendler: "store",
  taxi: "taxi", taxis: "taxi", transport: "taxi",
  hotel: "hotel", hotels: "hotel", unterkünfte: "hotel", unterkunft: "hotel",
  event: "event", events: "event",
  dienstleister: "service", dienstleistungen: "service", service: "service", services: "service",
  inserate: "message", inserat: "message", ads: "message", kleinanzeigen: "message",
};

export const mallDirectory: SceneBuilder = ({ ctx, clipPrefix, inputs, clipDurationSec, clipStartSec }) => {
  const accent = accentFor(inputs);
  const violet = themeColors.secondary; // #a855f7
  const pink = themeColors.accent;      // #ec4899
  const palette = [accent, violet, pink, accent, violet, pink, accent, violet];

  const isVertical = ctx.format === "9:16";
  const headline = inputs.text ?? "Alle Shops deiner Stadt. Unter einem Dach.";
  const signText = String(inputs.extras?.brand ?? brandName).toUpperCase();
  const tagline = inputs.extras?.tagline != null ? String(inputs.extras.tagline) : "DEIN DIGITALES EINKAUFSZENTRUM";

  const items = (inputs.items?.length
    ? inputs.items
    : [
        "Restaurants | Tisch buchen | shop",
        "Shops | Lokal kaufen | store",
        "Taxis | Sofort fahren | taxi",
        "Hotels | Übernachten | hotel",
        "Events | Tickets sichern | event",
        "Dienstleister | Termin buchen | service",
      ]
  ).slice(0, isVertical ? 6 : 8);

  const cols = isVertical ? 2 : 4;
  const lg = typeSize("lg", ctx.format);
  const md = typeSize("md", ctx.format);
  const sm = typeSize("sm", ctx.format);

  const wrapId = `${clipPrefix}-wrap`;
  const signId = `${clipPrefix}-sign`;
  const signLabelId = `${clipPrefix}-signlabel`;
  const headId = `${clipPrefix}-head`;
  const sweepId = `${clipPrefix}-sweep`;
  const facadeId = `${clipPrefix}-facade`;

  const tileIds = items.map((_, i) => `${clipPrefix}-tile-${i}`);
  const awningIds = items.map((_, i) => `${clipPrefix}-awn-${i}`);
  const glowIds = items.map((_, i) => `${clipPrefix}-glow-${i}`);
  const iconIds = items.map((_, i) => `${clipPrefix}-icon-${i}`);
  const labelIds = items.map((_, i) => `${clipPrefix}-label-${i}`);
  const capIds = items.map((_, i) => `${clipPrefix}-cap-${i}`);

  const t = makeTl(clipStartSec);
  const backdrop = brandBackdrop(ctx, `${clipPrefix}-bg`, { variant: "allyver", accent });

  const parsed = items.map((raw, i) => {
    const [title, caption, iconRaw] = splitItem(raw);
    const tt = (title ?? "").trim();
    const cap = (caption ?? "").trim();
    const iconKey = (iconRaw ?? "").trim() || (ICON_MAP[tt.toLowerCase()] ?? "store");
    const color = palette[i % palette.length] ?? accent;
    return { title: tt, caption: cap, iconKey, color };
  });

  // Adaptive tile sizing.
  const labelPx = isVertical ? md * 0.62 : sm * 0.74;
  const capPx = isVertical ? sm * 0.5 : sm * 0.52;
  const iconBox = isVertical ? 78 : 86;
  const iconPx = Math.round(iconBox * 0.5);
  const tileRadius = 22;
  const tilePadY = isVertical ? 26 : 30;

  function renderTile(idx: number, item: (typeof parsed)[number]): string {
    const color = item.color;
    return `<div id="${tileIds[idx]}" style="position:relative;display:flex;flex-direction:column;align-items:center;justify-content:flex-end;gap:${isVertical ? 12 : 14}px;padding:${tilePadY}px 18px 22px;border-radius:${tileRadius}px;background:linear-gradient(180deg, ${withAlpha(themeColors.fg, 0.06)} 0%, ${withAlpha(themeColors.bg, 0.85)} 100%);border:1px solid ${withAlpha(color, 0.4)};overflow:hidden;will-change:transform,opacity;box-shadow:0 18px 40px rgba(0,0,0,0.4);">
      <!-- Schaufenster-Glow (das „Licht" der Kachel) -->
      <div id="${glowIds[idx]}" style="position:absolute;inset:0;background:radial-gradient(120% 80% at 50% 0%, ${withAlpha(color, 0.45)} 0%, ${withAlpha(color, 0.08)} 45%, transparent 75%);"></div>
      <!-- Markise (gestreift, rollt von oben aus) -->
      <div id="${awningIds[idx]}" style="position:absolute;top:0;left:0;right:0;height:${isVertical ? 18 : 20}px;background:repeating-linear-gradient(90deg, ${color} 0 14px, ${withAlpha(themeColors.fg, 0.9)} 14px 28px);transform-origin:top center;box-shadow:0 6px 16px ${withAlpha(color, 0.5)};"></div>
      <!-- Icon-Disc -->
      <div id="${iconIds[idx]}" style="position:relative;z-index:2;width:${iconBox}px;height:${iconBox}px;border-radius:9999px;display:flex;align-items:center;justify-content:center;background:radial-gradient(circle at 30% 30%, ${withAlpha(color, 0.55)} 0%, ${withAlpha(color, 0.16)} 60%, ${withAlpha(themeColors.bg, 0.9)} 100%);border:1px solid ${withAlpha(color, 0.6)};box-shadow:0 0 26px ${withAlpha(color, 0.5)}, inset 0 1px 0 ${withAlpha(themeColors.fg, 0.2)};color:${themeColors.fg};">
        ${lucideIcon(item.iconKey, iconPx, themeColors.fg, 2.2)}
      </div>
      <!-- Label -->
      <div id="${labelIds[idx]}" style="position:relative;z-index:2;font-family:'Outfit',sans-serif;font-weight:${weights.black};font-size:${labelPx}px;line-height:1;letter-spacing:-0.5px;color:${themeColors.fg};text-align:center;text-transform:uppercase;">${escapeHtml(item.title)}</div>
      <!-- Caption -->
      ${item.caption ? `<div id="${capIds[idx]}" style="position:relative;z-index:2;font-family:'Inter',sans-serif;font-weight:${weights.semibold};font-size:${capPx}px;letter-spacing:1px;text-transform:uppercase;color:${withAlpha(color, 0.95)};text-align:center;">${escapeHtml(item.caption)}</div>` : `<div id="${capIds[idx]}" style="opacity:0;"></div>`}
    </div>`;
  }

  const tilesHtml = parsed.map((it, i) => renderTile(i, it)).join("");
  const signLabelPx = sm * 0.72;
  const signTagPx = sm * 0.5;

  const html = `<div id="${wrapId}" class="clip" data-start="${clipStartSec.toFixed(4)}" data-duration="${clipDurationSec.toFixed(4)}" style="position:absolute;inset:0;display:flex;flex-direction:column;align-items:center;justify-content:center;gap:${isVertical ? 28 : 34}px;padding:${isVertical ? "72px 44px" : "84px 110px"};overflow:hidden;">
    <!-- Dach-/Eingangsschild -->
    <div id="${signId}" style="position:relative;display:flex;flex-direction:column;align-items:center;gap:6px;padding:${isVertical ? "16px 38px" : "18px 52px"};border-radius:9999px;background:linear-gradient(90deg, ${withAlpha(accent, 0.16)} 0%, ${withAlpha(violet, 0.16)} 50%, ${withAlpha(pink, 0.16)} 100%);border:1px solid ${withAlpha(accent, 0.55)};box-shadow:0 0 48px ${withAlpha(accent, 0.45)};transform-origin:center center;">
      <div id="${signLabelId}" style="display:flex;align-items:center;gap:14px;font-family:'Outfit',sans-serif;font-weight:${weights.black};font-size:${signLabelPx}px;letter-spacing:6px;text-transform:uppercase;color:${themeColors.fg};white-space:nowrap;">
        <span style="width:18px;height:18px;border-radius:9999px;background:${accent};box-shadow:0 0 14px ${accent};"></span>
        <span>${escapeHtml(signText)}</span>
        <span style="width:18px;height:18px;border-radius:9999px;background:${pink};box-shadow:0 0 14px ${pink};"></span>
      </div>
      ${tagline ? `<div style="font-family:'Inter',sans-serif;font-weight:${weights.semibold};font-size:${signTagPx}px;letter-spacing:4px;text-transform:uppercase;color:${withAlpha(themeColors.fg, 0.7)};white-space:nowrap;">${escapeHtml(tagline)}</div>` : ""}
    </div>

    <!-- Headline -->
    <div id="${headId}" style="font-family:'Outfit',sans-serif;font-weight:${weights.black};font-size:${(isVertical ? md * 1.05 : lg * 0.9)}px;line-height:1.05;letter-spacing:-1.5px;color:${themeColors.fg};text-align:center;text-wrap:balance;max-width:${isVertical ? 900 : 1300}px;text-shadow:0 0 50px ${withAlpha(accent, 0.4)};">${escapeHtml(headline)}</div>

    <!-- Fassade aus Schaufenster-Kacheln -->
    <div id="${facadeId}" style="position:relative;display:grid;grid-template-columns:repeat(${cols}, 1fr);gap:${isVertical ? 18 : 22}px;width:100%;max-width:${isVertical ? 980 : 1500}px;">
      ${tilesHtml}
      <!-- Lichtstreifen über die Fassade -->
      <div id="${sweepId}" style="position:absolute;top:0;bottom:0;width:34%;background:linear-gradient(90deg, transparent 0%, ${withAlpha(themeColors.fg, 0.16)} 50%, transparent 100%);pointer-events:none;mix-blend-mode:screen;"></div>
    </div>
  </div>`;

  // ------------------------------------------------------------------
  // Animation
  // ------------------------------------------------------------------
  const init = [
    gsapSetLine(`#${signId}`, { opacity: 0, scaleX: 0 }),
    gsapSetLine(`#${signLabelId}`, { opacity: 0, y: -8 }),
    gsapSetLine(`#${headId}`, { opacity: 0, y: 24 }),
    gsapSetLine(`#${sweepId}`, { opacity: 0, xPercent: -120 }),
    ...tileIds.map((id) => gsapSetLine(`#${id}`, { opacity: 0, y: 44, scale: 0.9, transformOrigin: "50% 100%" })),
    ...awningIds.map((id) => gsapSetLine(`#${id}`, { scaleY: 0 })),
    ...glowIds.map((id) => gsapSetLine(`#${id}`, { opacity: 0 })),
    ...iconIds.map((id) => gsapSetLine(`#${id}`, { scale: 0, transformOrigin: "50% 50%" })),
    ...labelIds.map((id) => gsapSetLine(`#${id}`, { opacity: 0, y: 12 })),
    ...capIds.map((id, i) => (parsed[i].caption ? gsapSetLine(`#${id}`, { opacity: 0, y: 8 }) : "")),
  ].filter(Boolean).join("\n");

  const tweens: string[] = [
    t.to(`#${signId}`, { opacity: 1, scaleX: 1, duration: 0.6, ease: "raw:\"power3.out\"" }, 0.05),
    t.to(`#${signLabelId}`, { opacity: 1, y: 0, duration: 0.45, ease: "raw:\"power2.out\"" }, 0.25),
    t.to(`#${headId}`, { opacity: 1, y: 0, duration: 0.55, ease: "raw:\"power3.out\"" }, 0.4),
  ];

  // Stagger the storefronts opening.
  const tileStart = 0.7;
  const entryWindow = Math.min(1.8, Math.max(0.7, clipDurationSec * 0.5));
  const step = entryWindow / Math.max(parsed.length, 1);

  parsed.forEach((item, i) => {
    const at = tileStart + i * step;
    tweens.push(t.to(`#${tileIds[i]}`, { opacity: 1, y: 0, scale: 1, duration: 0.5, ease: "raw:\"back.out(1.6)\"" }, at));
    tweens.push(t.to(`#${awningIds[i]}`, { scaleY: 1, duration: 0.4, ease: "raw:\"power2.out\"" }, at + 0.06));
    // „Licht an": Schaufenster-Glow blendet ein.
    tweens.push(t.to(`#${glowIds[i]}`, { opacity: 1, duration: 0.5, ease: "raw:\"power2.out\"" }, at + 0.14));
    tweens.push(t.to(`#${iconIds[i]}`, { scale: 1, duration: 0.5, ease: "raw:\"back.out(2)\"" }, at + 0.1));
    tweens.push(t.to(`#${labelIds[i]}`, { opacity: 1, y: 0, duration: 0.4, ease: "raw:\"power2.out\"" }, at + 0.16));
    if (item.caption) tweens.push(t.to(`#${capIds[i]}`, { opacity: 1, y: 0, duration: 0.36, ease: "raw:\"power2.out\"" }, at + 0.22));
  });

  // Light sweep across the facade once the storefronts are lit.
  const sweepAt = tileStart + parsed.length * step + 0.1;
  const sweepEnd = clipDurationSec - 10 / 30 - 0.2;
  if (sweepEnd - sweepAt > 0.5) {
    tweens.push(t.to(`#${sweepId}`, { opacity: 1, duration: 0.2, ease: "raw:\"none\"" }, sweepAt));
    tweens.push(t.to(`#${sweepId}`, { xPercent: 320, duration: Math.min(1.2, sweepEnd - sweepAt - 0.2), ease: "raw:\"power1.inOut\"" }, sweepAt + 0.05));
    tweens.push(t.to(`#${sweepId}`, { opacity: 0, duration: 0.25, ease: "raw:\"none\"" }, sweepEnd - 0.1));
  }

  tweens.push(clipOutroFade(clipPrefix, clipDurationSec, clipStartSec));

  return combineSceneFragments([backdrop, { html, init, tweens }]);
};
